import React from 'react';
import { motion } from 'framer-motion';
import LogoImg from '../assets/logo.jpg'; 

const PortalLogin = ({ onBack }) => {
  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] bg-slate-950 flex flex-col md:flex-row overflow-hidden font-sans"
    >
      {/* --- LEFT SECTION: REFINED BRANDING --- */}
      <div className="relative hidden md:flex md:w-[60%] h-full bg-[#050810] items-center justify-center p-20 overflow-hidden border-r border-white/5">
        
        {/* Subtle Background Textures */}
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-red-600/5 via-transparent to-indigo-600/5"></div>
        
        {/* Top Branding Area */}
        <motion.div 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="absolute top-12 left-12 flex items-center gap-5"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-red-600/20 blur-lg rounded-full"></div>
            <img src={LogoImg} alt="Vidulaka" className="relative w-14 h-14 rounded-full border border-white/20 p-1" />
          </div>
          <div className="h-10 w-[1px] bg-white/10 mx-1"></div>
          <div>
            <h3 className="text-white font-black text-lg tracking-[0.1em] leading-none">VIDULAKA</h3>
            <p className="text-[8px] font-bold text-slate-500 uppercase tracking-[0.4em] mt-1.5">Matale Campus</p>
          </div>
        </motion.div>

        {/* Center Content Area */}
        <div className="relative z-10 w-full max-w-lg">
          <motion.div 
            initial={{ x: -30, opacity: 0 }} 
            animate={{ x: 0, opacity: 1 }} 
            transition={{ delay: 0.2 }}
            className="space-y-10"
          >
            {/* Tagline */}
            <div className="flex items-center gap-3">
              <span className="h-[1px] w-8 bg-red-600"></span>
              <span className="text-red-500 text-[10px] font-black uppercase tracking-[0.6em]">Nexus Enterprise Portal</span>
            </div>
            
            {/* Reduced Main Title */}
            <div className="space-y-2">
              <h1 className="text-5xl lg:text-6xl font-black text-white leading-[1.1] tracking-tight uppercase">
                INTELLIGENT <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-slate-500">SYSTEMS FOR</span> <br/>
                <span className="text-red-600 italic">MODERN LEARNING.</span>
              </h1>
            </div>
            
            {/* Refined Description */}
            <p className="text-slate-500 text-sm font-medium leading-relaxed max-w-sm border-l border-white/10 pl-6">
              Welcome to the centralized node of Vidulaka Matale. Access your personalized dashboard, synchronized resources, and academic analytics.
            </p>

            
          </motion.div>
        </div>

        {/* Bottom Area: SMART CLASS HIGHLIGHT */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="absolute bottom-12 left-12 flex items-center gap-6"
        >
          <div className="group relative bg-white/[0.03] backdrop-blur-sm border border-white/5 px-5 py-3 pr-8 flex items-center gap-4 transition-all hover:bg-white/[0.05] hover:border-red-600/30">
            <div className="relative w-2 h-2">
              <div className="absolute inset-0 bg-red-600 rounded-full animate-ping opacity-40"></div>
              <div className="relative w-2 h-2 bg-red-600 rounded-full shadow-[0_0_8px_#ef4444]"></div>
            </div>
            <div className="flex flex-col">
              <span className="text-[7px] font-black text-slate-500 uppercase tracking-[0.3em]">Platform Architecture</span>
              <span className="text-white font-black text-[10px] uppercase tracking-[0.2em] group-hover:text-red-500 transition-colors">
                Powered by <span className="text-red-600 italic">SMART CLASS</span>
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* --- RIGHT SECTION: UNIFIED LOGIN FORM (UNCHANGED BUT BALANCED) --- */}
      <div className="w-full md:w-[40%] h-full bg-white flex flex-col items-center justify-center p-8 md:p-20 relative">
        
        <button 
          onClick={onBack}
          className="absolute top-10 right-10 text-slate-400 hover:text-slate-950 transition-colors flex items-center gap-2 font-black text-[9px] uppercase tracking-[0.2em] active:scale-90"
        >
          EXIT PORTAL <span className="text-lg">✕</span>
        </button>

        <motion.div 
          initial={{ x: 20, opacity: 0 }} 
          animate={{ x: 0, opacity: 1 }} 
          transition={{ delay: 0.3 }}
          className="w-full max-w-sm space-y-10"
        >
          <div className="space-y-3">
            <h2 className="text-4xl font-black text-slate-950 uppercase tracking-tighter">Sign In</h2>
            <div className="w-12 h-1 bg-red-600"></div>
          </div>

          <form className="space-y-7" onSubmit={(e) => e.preventDefault()}>
            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Credential ID</label>
              <input 
                type="text" 
                placeholder="REG-000000"
                className="w-full bg-transparent border-b border-slate-200 px-0 py-3 text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-red-600 transition-all font-mono text-lg"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Security Key</label>
              <input 
                type="password" 
                placeholder="••••••••••••"
                className="w-full bg-transparent border-b border-slate-200 px-0 py-3 text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-red-600 transition-all font-mono text-lg"
              />
            </div>

            <div className="flex items-center justify-between py-1">
               <label className="flex items-center gap-3 cursor-pointer group">
                  <input type="checkbox" className="hidden" />
                  <div className="w-4 h-4 border border-slate-200 group-hover:border-red-600 transition-colors"></div>
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Keep Active</span>
               </label>
               <button className="text-[9px] font-black text-red-600 uppercase tracking-widest hover:text-slate-950 transition-colors">Recovery</button>
            </div>

            <button className="w-full bg-slate-950 text-white py-6 font-black uppercase tracking-[0.4em] text-[10px] hover:bg-red-600 transition-all duration-500 shadow-xl active:scale-[0.98] mt-4">
               AUTHORIZE ACCESS →
            </button>
          </form>

          <div className="pt-10 border-t border-slate-50 text-center">
             <p className="text-[8px] font-black text-slate-300 uppercase tracking-[0.3em] leading-relaxed">
                Encrypted Connection • Version 2.0.6 <br/>
                Matale Node Center
             </p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default PortalLogin;